/**
 * Conversation List Page - All conversations across channels with filters
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversationsApi, Conversation } from '../api/conversations';
import { useWebSocketEvent } from '../hooks/useWebSocket';

interface ConversationListResponse {
  data: Conversation[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

const CHANNEL_ICONS: Record<string, string> = {
  telegram: '📱',
  facebook: '👤',
  instagram: '📷',
  viber: '💬',
  email: '✉️',
};

function getChannelIcon(channel: string) {
  return CHANNEL_ICONS[channel.toLowerCase()] || '💬';
}

function formatRelativeDate(dateString: string | null) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (minutes < 1) return 'щойно';
  if (minutes < 60) return `${minutes} хв тому`;
  if (hours < 24) return `${hours} год тому`;
  return date.toLocaleDateString('uk-UA');
}

export default function ConversationList() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState({
    channel: '',
    status: '',
    search: '',
  });

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: ConversationListResponse =
        await conversationsApi.getList({
          page: pagination.page,
          per_page: pagination.per_page,
          channel: filters.channel || undefined,
          status: filters.status || undefined,
          search: filters.search || undefined,
        });

      setConversations(response.data);
      setPagination({
        page: response.page,
        per_page: response.per_page,
        total: response.total,
        pages: response.pages,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Помилка завантаження');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
  }, [pagination.page, filters]);

  useWebSocketEvent('new_message', (event) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === event.conversation_id
          ? { ...conv, last_message_at: event.message.sent_at }
          : conv
      )
    );
  });

  useWebSocketEvent('conversation_update', (event) => {
    if (
      event.update_type === 'bot_enabled' ||
      event.update_type === 'bot_disabled'
    ) {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === event.conversation_id
            ? { ...conv, bot_enabled: event.update_type === 'bot_enabled' }
            : conv
        )
      );
    }
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const visiblePages = (() => {
    const total = pagination.pages;
    const cur = pagination.page;
    const pages: number[] = [];
    for (let p = 1; p <= total; p++) {
      if (p === 1 || p === total || Math.abs(p - cur) <= 2) pages.push(p);
    }
    return pages;
  })();

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>Переписки</h1>
        <p style={{ color: '#8c8c8c', margin: '4px 0 0' }}>
          Всі розмови з клієнтами через різні канали
        </p>
      </div>

      {/* Filters */}
      <div style={cardStyle}>
        <div style={filtersGridStyle}>
          <div>
            <label style={labelStyle}>Канал</label>
            <select
              value={filters.channel}
              onChange={(e) => handleFilterChange('channel', e.target.value)}
              style={inputStyle}
            >
              <option value="">Всі канали</option>
              <option value="telegram">Telegram</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="viber">Viber</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Статус</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={inputStyle}
            >
              <option value="">Всі статуси</option>
              <option value="ACTIVE">Активні</option>
              <option value="CLOSED">Закриті</option>
            </select>
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Пошук</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Ім'я клієнта або email..."
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Results count */}
      <div style={{ margin: '16px 0', fontSize: 13, color: '#8c8c8c' }}>
        Знайдено: {pagination.total} розмов
      </div>

      {/* Loading state */}
      {loading && (
        <div style={{ ...cardStyle, padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
          Завантаження...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={errorBoxStyle}>
          <div style={{ color: '#cf1322' }}>{error}</div>
          <button onClick={loadConversations} style={errorLinkStyle}>
            Спробувати ще раз
          </button>
        </div>
      )}

      {/* Conversations table */}
      {!loading && !error && (
        <div style={{
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #f0f0f0',
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <th style={thStyle}>Клієнт</th>
                <th style={thStyle}>Канал</th>
                <th style={thStyle}>Замовлення</th>
                <th style={thStyle}>Статус</th>
                <th style={thStyle}>Остання активність</th>
                <th style={thStyle}>Бот</th>
              </tr>
            </thead>
            <tbody>
              {conversations.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
                    Розмов не знайдено
                  </td>
                </tr>
              ) : (
                conversations.map((conv) => (
                  <tr
                    key={conv.id}
                    onClick={() => navigate(`/conversations/${conv.id}`)}
                    style={{
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                  >
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500 }}>
                        {conv.client_name || 'Невідомий клієнт'}
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                        {conv.channel_user_id}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 16, marginRight: 6 }}>
                        {getChannelIcon(conv.channel)}
                      </span>
                      <span style={{ textTransform: 'capitalize' }}>{conv.channel}</span>
                    </td>
                    <td style={tdStyle}>
                      {conv.order_id ? (
                        <span style={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          background: '#f5f5f5',
                          padding: '2px 6px',
                          borderRadius: 4,
                        }}>
                          {conv.order_id}
                        </span>
                      ) : (
                        <span style={{ color: '#d9d9d9' }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <Badge
                        text={conv.status === 'ACTIVE' ? 'Активна' : 'Закрита'}
                        color={conv.status === 'ACTIVE' ? '#52c41a' : '#8c8c8c'}
                      />
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: '#595959', fontSize: 13 }}>
                        {formatRelativeDate(conv.last_message_at)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <Badge
                        text={conv.bot_enabled ? '🤖 Увімкнений' : '👤 Вимкнений'}
                        color={conv.bot_enabled ? '#1677ff' : '#faad14'}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div style={paginationContainerStyle}>
              <div style={{ fontSize: 13, color: '#595959' }}>
                Показано{' '}
                <strong>
                  {(pagination.page - 1) * pagination.per_page + 1}
                </strong>
                {' – '}
                <strong>
                  {Math.min(
                    pagination.page * pagination.per_page,
                    pagination.total
                  )}
                </strong>
                {' з '}
                <strong>{pagination.total}</strong>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: Math.max(1, prev.page - 1),
                    }))
                  }
                  disabled={pagination.page === 1}
                  style={paginationBtnStyle(false, pagination.page === 1)}
                >
                  ←
                </button>
                {visiblePages.map((page, idx, arr) => {
                  const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={page}>
                      {showEllipsis && (
                        <span style={{ padding: '6px 8px', color: '#8c8c8c' }}>…</span>
                      )}
                      <button
                        onClick={() =>
                          setPagination((prev) => ({ ...prev, page }))
                        }
                        style={paginationBtnStyle(page === pagination.page, false)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: Math.min(prev.pages, prev.page + 1),
                    }))
                  }
                  disabled={pagination.page === pagination.pages}
                  style={paginationBtnStyle(false, pagination.page === pagination.pages)}
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 500,
      background: `${color}15`,
      color: color,
      border: `1px solid ${color}40`,
    }}>
      {text}
    </span>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  padding: 16,
};

const filtersGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 12,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: '#595959',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 12px',
  border: '1px solid #d9d9d9',
  borderRadius: 6,
  fontSize: 14,
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorBoxStyle: React.CSSProperties = {
  background: '#fff2f0',
  border: '1px solid #ffccc7',
  borderRadius: 8,
  padding: 16,
};

const errorLinkStyle: React.CSSProperties = {
  marginTop: 8,
  background: 'transparent',
  border: 'none',
  color: '#cf1322',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 13,
  color: '#595959',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
};

const paginationContainerStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderTop: '1px solid #f0f0f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const paginationBtnStyle = (active: boolean, disabled: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  border: `1px solid ${active ? '#1677ff' : '#d9d9d9'}`,
  background: active ? '#e6f4ff' : '#fff',
  color: active ? '#1677ff' : '#595959',
  borderRadius: 6,
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  minWidth: 36,
});
